import React, { useEffect, useState } from 'react';

/**
 * [PURPOSE]: Epic confetti celebration for level-ups
 * [WHY]: Players deserve visual celebration for character progression
 * [INTEGRATION]: Triggered from App.jsx when level-up completes
 *
 * Features:
 * - 50 confetti particles with random trajectories
 * - D&D-themed colors (gold, purple, cyan, red, green)
 * - Physics-based falling animation
 * - Auto-dismisses after 4 seconds
 * - Performance optimized with CSS transforms
 */

const LevelUpConfetti = ({ show, onComplete, characterLevel }) => {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (show) {
      // [PARTICLE GENERATION]: Create 50 confetti particles with random properties
      const newParticles = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        // [POSITIONING]: Random starting position across top of screen
        left: Math.random() * 100,
        // [COLORS]: D&D themed palette - gold, purple, cyan, red, green
        color: ['#FFD700', '#A855F7', '#06B6D4', '#EF4444', '#10B981'][Math.floor(Math.random() * 5)],
        // [SIZE]: Varied particle sizes (8-16px)
        size: Math.random() * 8 + 8,
        // [PHYSICS]: Random rotation and animation duration
        rotation: Math.random() * 360,
        animationDuration: Math.random() * 2 + 2, // 2-4 seconds
        // [TRAJECTORY]: Random horizontal drift
        drift: (Math.random() - 0.5) * 200, // -100px to +100px drift
        // [ANIMATION DELAY]: Stagger particle release
        delay: Math.random() * 0.3
      }));

      setParticles(newParticles);

      // [AUTO DISMISS]: Clear confetti after 4 seconds
      const timer = setTimeout(() => {
        setParticles([]);
        onComplete?.();
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [show, onComplete]);

  if (!show || particles.length === 0) return null;

  return (
    <div
      className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden"
      aria-hidden="true"
    >
      {/* [CELEBRATION MESSAGE]: Epic level-up announcement */}
      <div
        className="absolute top-1/4 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
        style={{
          animation: 'levelUpAnnounce 2s ease-out forwards'
        }}
      >
        <div className="text-center">
          <div className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-purple-400 to-cyan-400 drop-shadow-2xl">
            LEVEL {characterLevel}!
          </div>
          <div className="text-2xl font-bold text-white mt-2 drop-shadow-lg">
            ⭐ Character Advanced! ⭐
          </div>
        </div>
      </div>

      {/* [CONFETTI PARTICLES]: Render all particles */}
      {particles.map(particle => (
        <div
          key={particle.id}
          className="absolute"
          style={{
            left: `${particle.left}%`,
            top: '-20px',
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            backgroundColor: particle.color,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            boxShadow: `0 0 ${particle.size}px ${particle.color}`,
            transform: `rotate(${particle.rotation}deg)`,
            animation: `confettiFall ${particle.animationDuration}s ease-in forwards`,
            animationDelay: `${particle.delay}s`,
            '--drift': `${particle.drift}px`,
            '--rotation': `${Math.random() * 720 - 360}deg`
          }}
        />
      ))}

      {/* [INLINE STYLES]: CSS animations for performance */}
      <style jsx>{`
        @keyframes confettiFall {
          0% {
            transform: translateY(0) translateX(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) translateX(var(--drift)) rotate(var(--rotation));
            opacity: 0;
          }
        }

        @keyframes levelUpAnnounce {
          0% {
            transform: translate(-50%, -50%) scale(0) rotate(-10deg);
            opacity: 0;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.2) rotate(5deg);
            opacity: 1;
          }
          70% {
            transform: translate(-50%, -50%) scale(0.95) rotate(-2deg);
            opacity: 1;
          }
          80% {
            transform: translate(-50%, -50%) scale(1.05) rotate(1deg);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(1) rotate(0deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default LevelUpConfetti;
